import { useState, useRef, useEffect, useCallback } from "react";
import { galleryImages, galleryCategories, GalleryCategory } from "@/mocks/gallery";
import { useScrollFadeIn } from "@/hooks/useScrollFadeIn";

export default function GallerySection() {
  const [activeCategory, setActiveCategory] = useState<GalleryCategory>("normal");
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  // Track which restricted categories have been confirmed this session
  const [unlockedCategories, setUnlockedCategories] = useState<Set<GalleryCategory>>(new Set());
  // Pending category to switch to after confirmation
  const [pendingCategory, setPendingCategory] = useState<GalleryCategory | null>(null);
  // 著作権保護: 長押し警告表示フラグ
  const [showProtectWarning, setShowProtectWarning] = useState(false);
  const protectWarningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const thumbRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const MOBILE_INITIAL_COUNT = 6;
  const DESKTOP_INITIAL_COUNT = 9;
  const touchStartX = useRef<number | null>(null);

  const filtered = activeCategory === "all"
    ? galleryImages
    : galleryImages.filter((img) => img.category === activeCategory);

  const currentCategoryInfo = galleryCategories.find((c) => c.key === activeCategory);
  const hasMobileMore = filtered.length > MOBILE_INITIAL_COUNT;
  const hasDesktopMore = filtered.length > DESKTOP_INITIAL_COUNT;

  const openLightbox = (id: number) => {
    setLightbox(id);
    document.body.style.overflow = "hidden";
  };
  const closeLightbox = () => {
    setLightbox(null);
    document.body.style.overflow = "";
  };

  const currentIndex = filtered.findIndex((img) => img.id === lightbox);

  const prev = () => {
    if (currentIndex > 0) setLightbox(filtered[currentIndex - 1].id);
  };

  const next = () => {
    if (currentIndex < filtered.length - 1) setLightbox(filtered[currentIndex + 1].id);
  };

  useEffect(() => {
    if (lightbox === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightbox, currentIndex, filtered.length]);

  // コンポーネントアンマウント時にoverflowを必ず解除
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // サムネイルが変わったら自動スクロール（縦・横両対応）
  useEffect(() => {
    if (lightbox === null) return;
    const el = thumbRefs.current[lightbox];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [lightbox]);

  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (touchStartX.current === null || touchStartY.current === null) return;
    const diffX = touchStartX.current - e.changedTouches[0].clientX;
    const diffY = touchStartY.current - e.changedTouches[0].clientY;
    // 横方向の動きが縦より大きい場合のみスワイプと判定
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 40) {
      if (diffX > 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // 著作権保護: 長押し検知 → 警告表示（スクロールは妨げない）
  const handleProtectedTouchStart = useCallback((e: React.TouchEvent) => {
    // e.preventDefault() を呼ばないことでスクロールを許可
    longPressTimer.current = setTimeout(() => {
      setShowProtectWarning(true);
      if (protectWarningTimer.current) clearTimeout(protectWarningTimer.current);
      protectWarningTimer.current = setTimeout(() => setShowProtectWarning(false), 1800);
    }, 600);
  }, []);

  const handleProtectedTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      if (protectWarningTimer.current) clearTimeout(protectWarningTimer.current);
    };
  }, []);

  const handleCategoryChange = (key: GalleryCategory) => {
    const catInfo = galleryCategories.find((c) => c.key === key);
    if (catInfo?.restricted && !unlockedCategories.has(key)) {
      // Show confirmation dialog
      setPendingCategory(key);
    } else {
      applyCategory(key);
    }
  };

  const applyCategory = (key: GalleryCategory) => {
    setActiveCategory(key);
    setLightbox(null);
    setExpanded(false);
  };

  const handleConfirm = () => {
    if (pendingCategory) {
      setUnlockedCategories((prev) => new Set([...prev, pendingCategory]));
      applyCategory(pendingCategory);
      setPendingCategory(null);
    }
  };

  const handleCancel = () => {
    setPendingCategory(null);
  };

  const isSensitiveUnlocked = unlockedCategories.has("sensitive");

  // "すべて"表示時にセンシティブが未承認かどうか
  const showBlurOnAll = activeCategory === "all" && !isSensitiveUnlocked;

  // センシティブコンテンツ表示中バッジを出す条件
  const showSensitiveBadge =
    isSensitiveUnlocked &&
    (activeCategory === "sensitive" || activeCategory === "all");

  const isCollapsed = !expanded;
  // スマホ・PC両方で初期枚数を制限（CSSで切り替え）
  const mobileDisplayed = isCollapsed ? filtered.slice(0, MOBILE_INITIAL_COUNT) : filtered;
  const desktopDisplayed = isCollapsed ? filtered.slice(0, DESKTOP_INITIAL_COUNT) : filtered;

  const { ref: headRef, style: headStyle } = useScrollFadeIn<HTMLDivElement>();
  const { ref: tabsDesktopRef, style: tabsDesktopStyle } = useScrollFadeIn<HTMLDivElement>({ delay: 0.1 });
  const { ref: tabsMobileRef, style: tabsMobileStyle } = useScrollFadeIn<HTMLDivElement>({ delay: 0.1 });
  const { ref: gridRef, style: gridStyle } = useScrollFadeIn<HTMLDivElement>({ delay: 0.2 });

  const pendingCatInfo = galleryCategories.find((c) => c.key === pendingCategory);

  return (
    <section id="gallery" className="py-20 md:py-32 bg-rose-200">
      <div className="max-w-6xl mx-auto px-4 md:px-10">
        {/* Header */}
        <div ref={headRef} style={headStyle} className="text-center mb-7 md:mb-12">
          <p className="text-xs tracking-[0.4em] text-stone-600 uppercase mb-2 md:mb-3">Gallery</p>
          <h2 className="font-en text-3xl md:text-4xl font-normal text-stone-800 tracking-widest">
            Gallery
          </h2>
          <div className="w-12 h-0.5 bg-stone-300 mx-auto mt-4 md:mt-5"></div>
        </div>

        {/* Filter Tabs — Desktop */}
        <div ref={tabsDesktopRef} style={tabsDesktopStyle} className="hidden md:flex flex-col items-center gap-2 mb-10">
          {/* Row 1: ノーマル / センシティブ */}
          <div className="flex items-center justify-center gap-2">
            {galleryCategories.filter((cat) => cat.key !== "all").map((cat) => {
              const count = galleryImages.filter((img) => img.category === cat.key).length;
              const isActive = activeCategory === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => handleCategoryChange(cat.key)}
                  className={`whitespace-nowrap px-4 py-2 text-xs tracking-widest transition-all duration-200 cursor-pointer rounded-full border flex items-center gap-1.5 ${
                    isActive
                      ? "bg-stone-800 text-white border-stone-800"
                      : "bg-transparent text-stone-700 border-stone-500 hover:border-stone-700 hover:text-stone-800"
                  }`}
                >
                  {cat.restricted && !unlockedCategories.has(cat.key) && (
                    <i className="ri-lock-line text-xs"></i>
                  )}
                  {cat.label}
                  <span className={`inline-flex items-center justify-center text-[10px] font-medium rounded-full min-w-[18px] h-[18px] px-1 ${
                    isActive ? "bg-white/20 text-white" : "bg-stone-100 text-stone-600"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Row 2: すべて */}
          <div className="flex items-center justify-center gap-2">
            {galleryCategories.filter((cat) => cat.key === "all").map((cat) => {
              const count = galleryImages.length;
              const isActive = activeCategory === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => handleCategoryChange(cat.key)}
                  className={`whitespace-nowrap px-4 py-2 text-xs tracking-widest transition-all duration-200 cursor-pointer rounded-full border flex items-center gap-1.5 ${
                    isActive
                      ? "bg-stone-800 text-white border-stone-800"
                      : "bg-transparent text-stone-700 border-stone-500 hover:border-stone-700 hover:text-stone-800"
                  }`}
                >
                  {cat.label}
                  <span className={`inline-flex items-center justify-center text-[10px] font-medium rounded-full min-w-[18px] h-[18px] px-1 ${
                    isActive ? "bg-white/20 text-white" : "bg-stone-100 text-stone-600"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Filter Tabs — Mobile */}
        <div ref={tabsMobileRef} style={tabsMobileStyle} className="flex md:hidden flex-col items-center gap-2 mb-7">
          {/* Row 1: ノーマル / センシティブ */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {galleryCategories.filter((cat) => cat.key !== "all").map((cat) => {
              const count = galleryImages.filter((img) => img.category === cat.key).length;
              const isActive = activeCategory === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => handleCategoryChange(cat.key)}
                  className={`whitespace-nowrap px-4 py-2 text-xs tracking-widest transition-all duration-200 cursor-pointer rounded-full border flex items-center gap-1.5 ${
                    isActive
                      ? "bg-stone-800 text-white border-stone-800"
                      : "bg-transparent text-stone-700 border-stone-500 hover:border-stone-700 hover:text-stone-800"
                  }`}
                >
                  {cat.restricted && !unlockedCategories.has(cat.key) && (
                    <i className="ri-lock-line text-xs"></i>
                  )}
                  {cat.label}
                  <span className={`inline-flex items-center justify-center text-[10px] font-medium rounded-full min-w-[18px] h-[18px] px-1 ${
                    isActive ? "bg-white/20 text-white" : "bg-stone-100 text-stone-600"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Row 2: すべて */}
          <div className="flex items-center justify-center gap-2">
            {galleryCategories.filter((cat) => cat.key === "all").map((cat) => {
              const count = galleryImages.length;
              const isActive = activeCategory === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => handleCategoryChange(cat.key)}
                  className={`whitespace-nowrap px-4 py-2 text-xs tracking-widest transition-all duration-200 cursor-pointer rounded-full border flex items-center gap-1.5 ${
                    isActive
                      ? "bg-stone-800 text-white border-stone-800"
                      : "bg-transparent text-stone-700 border-stone-500 hover:border-stone-700 hover:text-stone-800"
                  }`}
                >
                  {cat.label}
                  <span className={`inline-flex items-center justify-center text-[10px] font-medium rounded-full min-w-[18px] h-[18px] px-1 ${
                    isActive ? "bg-white/20 text-white" : "bg-stone-100 text-stone-600"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Current category notice */}
        {showSensitiveBadge && (
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 border border-stone-200 rounded-full">
              <i className="ri-eye-line text-xs text-stone-600"></i>
              <span className="text-xs text-stone-600 tracking-widest">センシティブコンテンツを表示中</span>
            </div>
          </div>
        )}

        {/* Masonry Grid — Mobile (2列) */}
        <div
          ref={gridRef}
          key={`mobile-${activeCategory}`}
          style={{ ...gridStyle, columnCount: 2, columnGap: "12px" }}
          className="md:hidden"
        >
          {mobileDisplayed.map((img) => {
            const isBlurred = showBlurOnAll && img.category === "sensitive";
            const aspectRatio = img.width && img.height ? img.width / img.height : undefined;
            return (
              <div
                key={img.id}
                className="protected-image-wrap relative overflow-hidden bg-stone-100 cursor-pointer group mb-3 break-inside-avoid select-none"
                style={aspectRatio ? { aspectRatio: `${aspectRatio}` } : undefined}
                onClick={() => { if (isBlurred) { setPendingCategory("sensitive"); } else { openLightbox(img.id); } }}
                onContextMenu={(e) => e.preventDefault()}
                onTouchStart={handleProtectedTouchStart}
                onTouchEnd={handleProtectedTouchEnd}
                onTouchCancel={handleProtectedTouchEnd}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  className={`protected-image w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105 ${isBlurred ? "blur-xl scale-110" : ""}`}
                />
                {isBlurred ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/10">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80"><i className="ri-lock-line text-stone-600 text-lg"></i></div>
                    <span className="text-white text-xs tracking-widest drop-shadow">タップして表示</span>
                  </div>
                ) : (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 active:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"><i className="ri-zoom-in-line text-white text-xl"></i></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Masonry Grid — Desktop (3列) */}
        <div
          key={`desktop-${activeCategory}`}
          style={{ columnCount: 3, columnGap: "16px" }}
          className="hidden md:block"
        >
          {desktopDisplayed.map((img) => {
            const isBlurred = showBlurOnAll && img.category === "sensitive";
            const aspectRatio = img.width && img.height ? img.width / img.height : undefined;
            return (
              <div
                key={img.id}
                className="protected-image-wrap relative overflow-hidden bg-stone-100 cursor-pointer group mb-4 break-inside-avoid select-none"
                style={aspectRatio ? { aspectRatio: `${aspectRatio}` } : undefined}
                onClick={() => { if (isBlurred) { setPendingCategory("sensitive"); } else { openLightbox(img.id); } }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  className={`protected-image w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105 ${isBlurred ? "blur-xl scale-110" : ""}`}
                />
                {isBlurred ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/10">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80"><i className="ri-lock-line text-stone-600 text-lg"></i></div>
                    <span className="text-white text-xs tracking-widest drop-shadow">クリックして表示</span>
                  </div>
                ) : (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"><i className="ri-zoom-in-line text-white text-xl"></i></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* More / Collapse button — Mobile */}
        {hasMobileMore && (
          <div className="flex justify-center mt-7 md:hidden">
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="whitespace-nowrap flex items-center gap-2 px-7 py-3 text-sm tracking-widest border-2 border-stone-500 text-stone-600 rounded-full active:border-stone-700 active:text-stone-700 active:scale-[0.97] transition-all cursor-pointer"
            >
              {expanded ? (
                <><i className="ri-arrow-up-s-line text-base"></i>閉じる</>
              ) : (
                <><i className="ri-add-line text-base"></i>もっと見る（{filtered.length - MOBILE_INITIAL_COUNT}枚）</>
              )}
            </button>
          </div>
        )}

        {/* More / Collapse button — Desktop */}
        {hasDesktopMore && (
          <div className="hidden md:flex justify-center mt-8">
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="whitespace-nowrap flex items-center gap-2 px-8 py-3 text-xs tracking-widest border border-stone-500 text-stone-600 rounded-full hover:border-stone-700 hover:text-stone-700 transition-all cursor-pointer"
            >
              {expanded ? (
                <><i className="ri-arrow-up-s-line text-sm"></i>閉じる</>
              ) : (
                <><i className="ri-add-line text-sm"></i>もっと見る（{filtered.length - DESKTOP_INITIAL_COUNT}枚）</>
              )}
            </button>
          </div>
        )}

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-stone-600 text-sm tracking-widest">
            該当する写真がありません
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightbox !== null && currentIndex !== -1 && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close button */}
          <button
            className="fixed top-4 right-4 z-[10000] w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-stone-600 hover:text-stone-900 transition-all cursor-pointer"
            onClick={closeLightbox}
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          {/* Pinterest-style layout */}
          <div
            className="flex flex-col md:flex-row w-full h-full max-w-6xl mx-auto px-2 md:px-4 pt-10 md:py-12 gap-3 md:gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Main image area */}
            <div className="flex-1 flex flex-col gap-2 md:gap-3 min-h-0">
              {/* Image */}
              <div
                className="protected-image-wrap flex-1 flex items-center justify-center bg-stone-900/50 rounded-xl overflow-hidden select-none relative min-h-0"
                onContextMenu={(e) => e.preventDefault()}
                onTouchStart={handleProtectedTouchStart}
                onTouchEnd={handleProtectedTouchEnd}
                onTouchCancel={handleProtectedTouchEnd}
              >
                <img
                  key={filtered[currentIndex]?.id}
                  src={filtered[currentIndex]?.src}
                  alt={filtered[currentIndex]?.alt}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  className="protected-image max-w-full max-h-[calc(100vh-220px)] md:max-h-[calc(100vh-120px)] w-auto h-auto object-contain rounded-lg"
                  style={{ animation: "fadeInScale 0.25s ease" }}
                />

                {/* 透明な保護オーバーレイ — タッチ長押しをキャッチして保存を防ぐ */}
                <div
                  className="absolute inset-0 z-10"
                  style={{ WebkitTouchCallout: "none", userSelect: "none" }}
                  onContextMenu={(e) => e.preventDefault()}
                  onTouchStart={(e) => { handleProtectedTouchStart(e); handleTouchStart(e); }}
                  onTouchEnd={(e) => { handleProtectedTouchEnd(); handleTouchEnd(e); }}
                  onTouchCancel={handleProtectedTouchEnd}
                />

                {/* 長押し警告オーバーレイ */}
                {showProtectWarning && (
                  <div className="protect-flash absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-xl bg-black/70">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 border border-white/20">
                      <i className="ri-copyright-line text-white text-xl"></i>
                    </div>
                    <p className="text-white text-xs tracking-[0.2em] text-center leading-relaxed px-6">
                      この画像は著作権により保護されています<br />
                      <span className="text-white/60 text-[10px]">© All Rights Reserved</span>
                    </p>
                  </div>
                )}

                {/* Prev / Next overlays */}
                <button
                  className={`absolute left-2 md:left-3 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-stone-600 hover:text-stone-900 transition-all cursor-pointer ${currentIndex === 0 ? "opacity-0 pointer-events-none" : "opacity-100"}`}
                  onClick={prev}
                >
                  <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
                </button>
                <button
                  className={`absolute right-2 md:right-3 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-stone-600 hover:text-stone-900 transition-all cursor-pointer ${currentIndex === filtered.length - 1 ? "opacity-0 pointer-events-none" : "opacity-100"}`}
                  onClick={next}
                >
                  <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
                </button>
              </div>

              {/* Counter */}
              <div className="flex items-center justify-center">
                <span className="text-white/50 text-xs tracking-[0.3em]">
                  {galleryCategories.find((c) => c.key === filtered[currentIndex]?.category)?.label}
                  　{currentIndex + 1} / {filtered.length}
                </span>
              </div>
            </div>

            {/* Bottom thumbnail strip — Mobile only */}
            <div className="flex md:hidden flex-col gap-1.5 flex-shrink-0">
              <p className="text-white/40 text-[10px] tracking-widest text-center">他の写真</p>
              <div
                className="overflow-x-auto pb-2"
                style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.2) transparent" }}
              >
                <div className="flex flex-row gap-2" style={{ width: "max-content" }}>
                  {filtered.map((img, idx) => (
                    <button
                      key={img.id}
                      ref={(el) => { thumbRefs.current[img.id] = el; }}
                      onClick={() => setLightbox(img.id)}
                      className={`relative rounded-lg overflow-hidden select-none cursor-pointer flex-shrink-0 transition-all duration-200 ${
                        img.id === lightbox
                          ? "ring-2 ring-white opacity-100 scale-[1.05]"
                          : "opacity-45 active:opacity-80"
                      }`}
                      style={{ width: "64px", height: "96px" }}
                      onContextMenu={(e) => e.preventDefault()}
                    >
                      <img
                        src={img.src}
                        alt={img.alt}
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        className="w-full h-full object-cover object-top pointer-events-none"
                      />
                      {img.id === lightbox && (
                        <div className="absolute inset-0 bg-white/10 rounded-lg" />
                      )}
                      <div className="absolute bottom-0.5 right-0.5 bg-black/50 text-white text-[9px] px-1 py-0.5 rounded-full">
                        {idx + 1}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right thumbnail strip — Desktop only */}
            <div className="hidden md:flex flex-col w-[160px] lg:w-[200px] h-full">
              <p className="text-white/40 text-xs tracking-widest mb-3 text-center">他の写真</p>
              <div className="flex-1 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.2) transparent" }}>
                <div className="flex flex-col gap-2">
                  {filtered.map((img, idx) => (
                    <button
                      key={img.id}
                      ref={(el) => { thumbRefs.current[img.id] = el; }}
                      onClick={() => setLightbox(img.id)}
                      className={`relative rounded-lg overflow-hidden select-none cursor-pointer flex-shrink-0 transition-all duration-200 ${
                        img.id === lightbox
                          ? "ring-2 ring-white ring-offset-1 ring-offset-transparent opacity-100 scale-[1.03]"
                          : "opacity-50 hover:opacity-80"
                      }`}
                      onContextMenu={(e) => e.preventDefault()}
                    >
                      <img
                        src={img.src}
                        alt={img.alt}
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        className="w-full h-auto object-cover object-top pointer-events-none"
                        style={{
                          aspectRatio: img.width && img.height ? `${img.width}/${img.height}` : "3/4",
                        }}
                      />
                      {img.id === lightbox && (
                        <div className="absolute inset-0 bg-white/10 rounded-lg" />
                      )}
                      <div className="absolute bottom-1 right-1 bg-black/40 text-white text-[10px] px-1.5 py-0.5 rounded-full tracking-wide">
                        {idx + 1}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Age / Content Confirmation Dialog */}
      {pendingCategory !== null && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-6"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
        >
          <div
            className="bg-white w-full max-w-sm rounded-lg p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5 rounded-full bg-stone-100">
              <i className="ri-eye-off-line text-xl text-stone-600"></i>
            </div>
            <p className="text-xs tracking-[0.3em] text-stone-600 uppercase mb-2">
              {pendingCatInfo?.label}
            </p>
            <h3 className="text-base font-light text-stone-800 tracking-widest mb-3">
              センシティブコンテンツ
            </h3>
            <p className="text-xs text-stone-600 leading-relaxed tracking-wide mb-7">
              このカテゴリには成人向けのコンテンツが含まれます。18歳以上の方のみご覧いただけます。続けますか？
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleConfirm}
                className="whitespace-nowrap flex-1 py-2.5 text-xs tracking-widest bg-stone-800 text-white rounded-full hover:bg-stone-700 transition-all cursor-pointer"
              >
                確認して表示
              </button>
              <button
                onClick={handleCancel}
                className="whitespace-nowrap flex-1 py-2.5 text-xs tracking-widest border border-stone-500 text-stone-600 rounded-full hover:border-stone-700 transition-all cursor-pointer"
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
